import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  TrendingUp, DollarSign, Users, AlertTriangle, RefreshCw,
  ArrowUpRight, ArrowDownRight, Clock
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FinancialTabNav from '../components/FinancialTabNav';
import api from '../services/api';

const AdminFinancialForecast = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/admin/financial/forecast');
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load forecast data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Sidebar>
        <FinancialTabNav />
        <div className="flex items-center justify-center py-20"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
      </Sidebar>
    );
  }

  if (!data) return null;

  const { current, history, forecast, risk } = data;
  const allMonths = [...history, ...forecast];
  const maxSubs = Math.max(...allMonths.map(m => m.subscribers || m.projected_subscribers || 0), 1);

  return (
    <Sidebar>
      <FinancialTabNav />
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        <div className="max-w-7xl mx-auto space-y-6">

          {/* Current Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Current MRR</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${current.mrr.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Projected ARR</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${current.arr.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Active Subscribers</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{current.subscribers}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Avg MRR / Subscriber</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">${current.avg_mrr_per_sub}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-sm text-gray-500">Monthly Growth</p>
              <p className={`text-2xl font-bold mt-1 flex items-center gap-1 ${current.monthly_growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {current.monthly_growth_rate >= 0 ? <ArrowUpRight className="w-5 h-5" /> : <ArrowDownRight className="w-5 h-5" />}
                {Math.abs(current.monthly_growth_rate)}%
              </p>
            </div>
          </div>

          {/* Subscriber Chart: History + Forecast */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Subscriber Trend & Forecast</h3>
            <p className="text-sm text-gray-400 mb-4">Past 12 months (solid) + next 12 months projected (dashed)</p>
            <div className="space-y-1.5">
              {allMonths.map((m, idx) => {
                const subs = m.subscribers || m.projected_subscribers || 0;
                const pct = (subs / maxSubs) * 100;
                const isForecast = idx >= history.length;
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-20 flex-shrink-0 text-right">{m.short || m.month?.split(' ')[0]}</span>
                    <div className="flex-1 h-5 bg-gray-50 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full flex items-center justify-end pr-2 transition-all duration-300 ${
                          isForecast ? 'bg-blue-200 border border-dashed border-blue-400' : 'bg-blue-500'
                        }`}
                        style={{ width: `${Math.max(pct, 6)}%` }}
                      >
                        {pct > 15 && <span className={`text-xs font-medium ${isForecast ? 'text-blue-700' : 'text-white'}`}>{subs}</span>}
                      </div>
                    </div>
                    {pct <= 15 && <span className="text-xs text-gray-500 w-8">{subs}</span>}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-6 mt-4 text-xs text-gray-400">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-500 rounded-full" /> Actual</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-blue-200 border border-dashed border-blue-400 rounded-full" /> Projected</span>
            </div>
          </div>

          {/* MRR Forecast */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Forecast (Next 12 Months)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Month</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-500">Projected Subscribers</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-500">Projected MRR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {forecast.map((f, idx) => (
                    <tr key={idx} className="hover:bg-gray-50/50">
                      <td className="px-3 py-2 text-gray-700">{f.month}</td>
                      <td className="px-3 py-2 text-right text-gray-900 font-medium">{f.projected_subscribers}</td>
                      <td className="px-3 py-2 text-right text-green-600 font-semibold">${f.projected_mrr.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Risk Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-gray-900">Trials Active</h3>
              </div>
              <p className="text-3xl font-bold text-amber-600">{risk.trials_ending}</p>
              <p className="text-xs text-gray-400 mt-1">May convert or churn</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold text-gray-900">Past Due</h3>
              </div>
              <p className="text-3xl font-bold text-red-600">{risk.past_due}</p>
              <p className="text-xs text-gray-400 mt-1">At risk of churning</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <h3 className="font-semibold text-gray-900">12-Month ARR Forecast</h3>
              </div>
              <p className="text-3xl font-bold text-green-600">
                ${forecast.length > 0 ? (forecast[forecast.length - 1].projected_mrr * 12).toLocaleString() : 0}
              </p>
              <p className="text-xs text-gray-400 mt-1">Based on current growth trend</p>
            </div>
          </div>

        </div>
      </div>
    </Sidebar>
  );
};

export default AdminFinancialForecast;
